import { createRecordType, defineMigrations } from "@supersig/store";
import { T } from "@supersig/validate";
import { nanoid } from "nanoid";
import { getIndexAbove } from "../../utils/reordering/reordering";
import { idValidator } from "../misc/id-validator";
//
// Product Variables
//
const productVariablesInline = T.object({
    source: T.literal("inline"),
    variables: T.any
});
const productVariablesSdk = T.object({
    source: T.literal("sdk")
});
const productVariables = T.union("source", {
    inline: productVariablesInline,
    sdk: productVariablesSdk
});
//
// Purchase Behavior
//
export const PaywallProductValidator = T.model("paywall_product", T.object({
    id: idValidator("paywall_product"),
    typeName: T.literal("paywall_product"),
    // Store, where the product is from
    // can be optional for legacy products. However,
    // when purchasing from a stripe store, this will be set.
    // This must match the `store prefixed product` part of the
    // string. Ex: `stripe`, `app-store`, `play-store`
    // purchaseBehavior,
    store: T.literalEnum("app-store", "play-store", "stripe", "paddle").nullable(),
    // If provided, we'll store the variables for the product
    // directly in the paywall and we'll template with them
    // when we get the products message in the pjs controller.
    productVariables,
    // Unprefixed identifier
    identifier: T.string,
    index: T.string
}));
const Versions = {
    AddIndex: 1,
    AddStoreAndProductVariables: 2
};
const primaryIndex = "a0";
const secondaryIndex = getIndexAbove(primaryIndex);
const tertiaryIndex = getIndexAbove(secondaryIndex);
const idToIndex = {
    [createPaywallProductId("primary")]: primaryIndex,
    [createPaywallProductId("secondary")]: secondaryIndex,
    [createPaywallProductId("tertiary")]: tertiaryIndex
};
export const SWPaywallProductRecordType = createRecordType("paywall_product", {
    migrations: defineMigrations({
        currentVersion: Versions.AddStoreAndProductVariables,
        migrators: {
            [Versions.AddIndex]: {
                up: ({ ...rest })=>{
                    const index = idToIndex[rest.id];
                    console.log(`[!] index`, index, rest.id, rest);
                    return {
                        ...rest,
                        index: idToIndex[rest.id]
                    };
                },
                down: ({ index, ...rest })=>({
                        ...rest
                    })
            },
            [Versions.AddStoreAndProductVariables]: {
                up: ({ ...rest })=>({
                        ...rest,
                        productVariables: {
                            source: "sdk"
                        },
                        store: null
                    }),
                down: ({ productVariables, store, ...rest })=>({
                        ...rest
                    })
            }
        }
    }),
    validator: PaywallProductValidator,
    scope: "document"
}).withDefaultProperties(()=>({
        store: null,
        productVariables: {
            source: "sdk"
        }
    }));
export function isPaywallProduct(record) {
    if (!record) return false;
    return record.typeName === "paywall_product";
}
export function isPaywallProductId(id) {
    if (!id) return false;
    return id.startsWith("paywall_product:");
}
/** @public */ export function createPaywallProductId(id) {
    return `paywall_product:${id ?? nanoid()}`;
}
export function getProductPrefixFromId(id) {
    if (!id) return "";
    return id.split(":")[1] ?? null;
}
export const castPaywallProductId = (id)=>{
    return {
        toReferenceName () {
            return id.replace(/^paywall_product:/, "");
        }
    };
};
